/**
 * Procedural adaptive score.
 *
 * Continuous layers (sub, drone, pad, tension) run permanently and are crossfaded
 * by state; percussion is scheduled on a 16th grid with a 250 ms lookahead. All
 * material is synthesised — there is not a sample anywhere in here.
 *
 * Restrained by design: the music bus sits well under the weapons bus and ducks
 * hard on every shot, so the score is felt rather than heard.
 */
import { makeRng } from '../core/Rand.js';
import { gain, bq, playData } from './synth/nodes.js';
import { noiseSlice } from './synth/noise.js';
import { sineSweep, applyAD, applyExpDecay, bandpass, normalise as normArr, modal } from './synth/dsp.js';

const ROOT = 55.0;                       // A1
const MINOR = [0, 3, 7, 10, 12];
const MAJOR = [0, 4, 7, 11, 12];
const semi = (n) => Math.pow(2, n / 12);

export const MUSIC_STATES = ['menu', 'ambient', 'combat', 'victory', 'defeat'];

const STATE_MIX = {
  menu: { sub: 0.22, drone: 0.50, pad: 0.34, tension: 0.05, perc: 0.10, bpm: 66, scale: MINOR, transpose: 0 },
  ambient: { sub: 0.14, drone: 0.22, pad: 0.09, tension: 0.02, perc: 0.00, bpm: 62, scale: MINOR, transpose: 0 },
  combat: { sub: 0.46, drone: 0.34, pad: 0.18, tension: 0.46, perc: 0.72, bpm: 88, scale: MINOR, transpose: 0 },
  victory: { sub: 0.30, drone: 0.28, pad: 0.55, tension: 0.05, perc: 0.34, bpm: 76, scale: MAJOR, transpose: 5 },
  defeat: { sub: 0.40, drone: 0.44, pad: 0.38, tension: 0.14, perc: 0.08, bpm: 52, scale: MINOR, transpose: -2 },
};

// 16-step percussion grids: [kick, hat, hit]
const PATTERNS = {
  combat: {
    kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    hit: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
  },
  menu: {
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    hit: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  victory: {
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
    hit: [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
  },
  defeat: {
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    hit: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  ambient: { kick: new Array(16).fill(0), hat: new Array(16).fill(0), hit: new Array(16).fill(0) },
};

export class Music {
  /**
   * @param {BaseAudioContext} ac
   * @param {import('./Buses.js').Buses} buses
   */
  constructor(ac, buses, { seed = 0x51de } = {}) {
    this.ac = ac;
    this.buses = buses;
    this.rng = makeRng(seed);
    // null, not 'ambient': every layer gain starts at 0, so the first set() must
    // always run rather than being short-circuited as a no-op state change.
    this.state = null;
    this.mix = { ...STATE_MIX.ambient };
    this.bus = buses.input('music');
    this.out = gain(ac, 1);
    this.out.connect(this.bus);

    this._nodes = [];
    this._buildLayers();

    this.step = 0;
    this.bar = 0;
    this.nextStepTime = ac.currentTime + 0.12;
    this.lookahead = 0.28;
    this.running = true;
    this.chordIndex = 0;
  }

  _osc(type, freq, detune = 0) {
    const o = this.ac.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    if (detune) o.detune.value = detune;
    return o;
  }

  _buildLayers() {
    const ac = this.ac;
    const t = ac.currentTime;

    /* --- sub: a single low sine with a slow amplitude breath --- */
    this.subGain = gain(ac, 0);
    const sub = this._osc('sine', ROOT);
    const subLfo = this._osc('sine', 0.09);
    const subLfoG = gain(ac, 0.35);
    subLfo.connect(subLfoG).connect(this.subGain.gain);
    sub.connect(this.subGain).connect(this.out);
    this.sub = sub;

    /* --- drone: detuned saws through a slowly breathing lowpass --- */
    this.droneGain = gain(ac, 0);
    this.droneLP = bq(ac, 'lowpass', 420, 3.2);
    const dLfo = this._osc('sine', 0.045);
    const dLfoG = gain(ac, 260);
    dLfo.connect(dLfoG).connect(this.droneLP.frequency);
    this.droneOscs = [];
    this.droneMults = [];
    for (const [mult, det] of [[1, -7], [1, 9], [2, 4], [3, -5]]) {
      const o = this._osc('sawtooth', ROOT * mult, det);
      const g = gain(ac, 0.24 / mult);
      o.connect(g).connect(this.droneLP);
      this.droneOscs.push(o);
      this.droneMults.push(mult);
      this._nodes.push(o, g);
    }
    this.droneLP.connect(this.droneGain).connect(this.out);

    /* --- pad: a triangle chord, retuned on state change --- */
    this.padGain = gain(ac, 0);
    this.padLP = bq(ac, 'lowpass', 1600, 0.8);
    this.padOscs = [];
    for (let i = 0; i < 4; i++) {
      const o = this._osc('triangle', ROOT * 4 * semi(MINOR[i]), (i % 2 ? 6 : -6));
      const g = gain(ac, 0.13);
      o.connect(g).connect(this.padLP);
      this.padOscs.push({ o, g });
      this._nodes.push(o, g);
    }
    this.padLP.connect(this.padGain).connect(this.out);

    /* --- tension: shimmering bandpassed noise, combat only --- */
    this.tensionGain = gain(ac, 0);
    const nBuf = ac.createBuffer(1, Math.floor(ac.sampleRate * 2), ac.sampleRate);
    nBuf.copyToChannel(noiseSlice('pink', nBuf.length, makeRng(0x7e7)), 0);
    const nSrc = ac.createBufferSource();
    nSrc.buffer = nBuf;
    nSrc.loop = true;
    const tBP = bq(ac, 'bandpass', 2400, 4.5);
    const tLfo = this._osc('sine', 0.13);
    const tLfoG = gain(ac, 900);
    tLfo.connect(tLfoG).connect(tBP.frequency);
    nSrc.connect(tBP).connect(this.tensionGain).connect(this.out);
    this.tensionSrc = nSrc;

    this._starts = [sub, subLfo, dLfo, tLfo, nSrc, ...this.droneOscs, ...this.padOscs.map((p) => p.o)];
    for (const n of this._starts) { try { n.start(t); } catch { /* already started */ } }
    this._nodes.push(this.subGain, this.droneGain, this.padGain, this.tensionGain, this.droneLP, this.padLP, tBP);
  }

  /** Crossfade to a new state. Unknown states fall back to 'ambient'. */
  set(state) {
    const s = MUSIC_STATES.includes(state) ? state : 'ambient';
    if (s === this.state) return;
    this.state = s;
    const m = STATE_MIX[s];
    this.mix = m;
    const t = this.ac.currentTime;
    const tc = s === 'combat' ? 0.5 : 1.6;
    this.subGain.gain.setTargetAtTime(m.sub * 0.55, t, tc);
    this.droneGain.gain.setTargetAtTime(m.drone * 0.30, t, tc);
    this.padGain.gain.setTargetAtTime(m.pad * 0.22, t, tc);
    this.tensionGain.gain.setTargetAtTime(m.tension * 0.13, t, tc);

    // retune: transpose the root and swap the chord quality
    const root = ROOT * semi(m.transpose);
    this.sub.frequency.setTargetAtTime(root, t, tc);
    this.droneOscs.forEach((o, i) => {
      o.frequency.setTargetAtTime(root * this.droneMults[i], t, tc);
    });
    this.padOscs.forEach((p, i) => {
      p.o.frequency.setTargetAtTime(root * 4 * semi(m.scale[i % m.scale.length]), t, tc);
    });
    this.padLP.frequency.setTargetAtTime(s === 'victory' ? 2600 : s === 'defeat' ? 900 : 1600, t, tc);
  }

  /** Advance the step scheduler. */
  update() {
    if (!this.running) return;
    const ac = this.ac;
    const spb = 60 / (this.mix.bpm || 70);
    const stepDur = spb / 4;
    let guard = 0;
    while (this.nextStepTime < ac.currentTime + this.lookahead && guard++ < 32) {
      this._schedule(this.step, this.nextStepTime);
      this.nextStepTime += stepDur;
      this.step = (this.step + 1) % 16;
      if (this.step === 0) {
        this.bar++;
        this.chordIndex = (this.chordIndex + 1) % 4;
      }
    }
    if (this.nextStepTime < ac.currentTime - 1) this.nextStepTime = ac.currentTime + 0.05;
  }

  _schedule(step, when) {
    const p = PATTERNS[this.state] || PATTERNS.ambient;
    const amp = this.mix.perc;
    if (amp <= 0.001) return;
    const rng = this.rng;
    if (p.kick[step] && rng() < 0.96) this._kick(when, amp * (0.85 + rng() * 0.3));
    if (p.hat[step] && rng() < 0.85) this._hat(when, amp * 0.35 * (0.6 + rng() * 0.6));
    if (p.hit[step] && rng() < 0.8) this._hit(when, amp * 0.5 * (0.7 + rng() * 0.5));
  }

  _kick(when, amp) {
    const ac = this.ac;
    const sr = ac.sampleRate;
    const d = sineSweep(sr, 0.32, 118, 41, 0.09, this.rng, 2.4);
    const g = gain(ac, amp * 0.42);
    const lp = bq(ac, 'lowpass', 220, 0.9);
    lp.connect(g).connect(this.out);
    playData(ac, d, lp, when, {});
  }

  _hat(when, amp) {
    const ac = this.ac;
    const sr = ac.sampleRate;
    const d = noiseSlice('white', Math.floor(sr * 0.09), this.rng);
    bandpass(d, sr, 8200, 1.4);
    applyAD(d, sr, 0.0008, 0.012 + this.rng() * 0.02);
    normArr(d, 1);
    const g = gain(ac, amp * 0.16);
    g.connect(this.out);
    playData(ac, d, g, when, {});
  }

  _hit(when, amp) {
    const ac = this.ac;
    const sr = ac.sampleRate;
    const base = 240 * semi(this.mix.transpose || 0);
    const d = modal(sr, 0.5, [
      [base, 1, 0.16], [base * 2.7, 0.4, 0.09], [base * 5.1, 0.2, 0.05], [base * 0.5, 0.6, 0.26],
    ], this.rng, 0.03);
    applyExpDecay(d, sr, 0.14);
    normArr(d, 1);
    const g = gain(ac, amp * 0.20);
    const bp = bq(ac, 'peaking', base * 2, 1.2, 4);
    bp.connect(g).connect(this.out);
    playData(ac, d, bp, when, {});
  }

  stop() {
    this.running = false;
    const t = this.ac.currentTime;
    for (const g of [this.subGain, this.droneGain, this.padGain, this.tensionGain]) {
      g.gain.setTargetAtTime(0, t, 0.4);
    }
  }

  dispose() {
    this.running = false;
    const t = this.ac.currentTime;
    for (const n of this._starts) { try { n.stop(t + 0.05); } catch { /* ignore */ } }
    for (const n of this._nodes) { try { n.disconnect(); } catch { /* ignore */ } }
  }
}
